const DRAG_THRESHOLD = 4;

/**
 * Horizontal drag-scroll for collection category pills.
 * Touch / trackpad use native overflow scroll; mouse / pen get grab-drag.
 * No arrow controls.
 */
class CollectionCategoriesSlider extends HTMLElement {
  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#track = this.querySelector('[data-collection-categories-track]');
    if (!this.#track) return;

    this.#track.addEventListener('pointerdown', this.#onPointerDown);
    this.#track.addEventListener('pointermove', this.#onPointerMove);
    this.#track.addEventListener('pointerup', this.#onPointerUp);
    this.#track.addEventListener('pointercancel', this.#onPointerUp);
    this.#track.addEventListener('click', this.#onClickCapture, true);
    this.#track.addEventListener('dragstart', this.#onDragStart);

    this.#scrollActiveIntoView();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#track?.removeEventListener('pointermove', this.#onPointerMove);
    this.#track?.removeEventListener('pointerup', this.#onPointerUp);
    this.#track?.removeEventListener('pointercancel', this.#onPointerUp);
    this.#track?.removeEventListener('click', this.#onClickCapture, true);
    this.#track?.removeEventListener('dragstart', this.#onDragStart);
  }

  /** @type {boolean} */
  #initialized = false;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {boolean} */
  #pointerActive = false;

  /** @type {boolean} */
  #dragging = false;

  /** @type {boolean} */
  #suppressClick = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragScrollLeft = 0;

  /** @type {number} */
  #activePointerId = -1;

  #scrollActiveIntoView() {
    const active = this.#track?.querySelector('.collection-categories__pill--active');
    if (!active || !this.#track) return;

    // Keep first / early pills left-aligned; center only when needed.
    const track = this.#track;
    const item = active.closest('.collection-categories__item') || active;
    requestAnimationFrame(() => {
      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.left >= trackRect.left && itemRect.right <= trackRect.right) return;

      const delta =
        itemRect.left + itemRect.width / 2 - (trackRect.left + trackRect.width / 2);
      track.scrollTo({ left: track.scrollLeft + delta, behavior: 'auto' });
    });
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.#track || event.button !== 0) return;
    // Touch already pans via overflow-x.
    if (event.pointerType === 'touch') return;
    if (this.#track.scrollWidth <= this.#track.clientWidth + 1) return;

    this.#pointerActive = true;
    this.#dragging = false;
    this.#suppressClick = false;
    this.#dragStartX = event.clientX;
    this.#dragScrollLeft = this.#track.scrollLeft;
    this.#activePointerId = event.pointerId;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#pointerActive || !this.#track || event.pointerId !== this.#activePointerId) return;

    const deltaX = event.clientX - this.#dragStartX;

    if (!this.#dragging) {
      if (Math.abs(deltaX) <= DRAG_THRESHOLD) return;
      this.#dragging = true;
      this.#track.classList.add('is-dragging');
      this.classList.add('is-dragging');

      try {
        this.#track.setPointerCapture(event.pointerId);
      } catch {
        // Ignore unsupported capture.
      }
    }

    this.#track.scrollLeft = this.#dragScrollLeft - deltaX;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#track || (this.#activePointerId !== -1 && event.pointerId !== this.#activePointerId)) {
      return;
    }

    const didDrag = this.#dragging;

    this.#pointerActive = false;
    this.#dragging = false;
    this.#activePointerId = -1;
    this.#track.classList.remove('is-dragging');
    this.classList.remove('is-dragging');

    try {
      if (this.#track.hasPointerCapture?.(event.pointerId)) {
        this.#track.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release errors.
    }

    if (didDrag) {
      this.#suppressClick = true;
      window.setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * @param {DragEvent} event
   */
  #onDragStart = (event) => {
    // Prevent native image / link drag ghost while sliding.
    event.preventDefault();
  };
}

if (!customElements.get('collection-categories-slider')) {
  customElements.define('collection-categories-slider', CollectionCategoriesSlider);
}
